/**
 * TON-specific helpers for the Mini App.
 *
 * Just format helpers + message builders. Curve quote math was removed
 * (CC-025 rollback): the on-chain `receive(Buy)` clamps + refunds
 * correctly, and Tonkeeper's pre-sign preview already shows the user
 * the actual amounts. Re-deriving the math off-chain was a
 * double-source-of-truth bug magnet — most notably the API hardcodes
 * `virtualTon = 30 TON`, but dev curves run with 0.5 TON, so any
 * off-chain quote was structurally wrong on dev.
 *
 * Use:
 *   - parseTon / formatTon — string ↔ nano conversion
 *   - buildBuyBody / buildClaimFeesBody — message cells
 *
 * For "TON in / jettons out" display, trust the wallet preview.
 */

import { Address, beginCell, Cell, toNano } from '@ton/core';
import { OP_BUY, OP_CLAIM_FEES, OP_JETTON_TRANSFER } from '@memepad/shared';

const NANO = 1_000_000_000n;

export function formatTon(nano: bigint, fractionDigits = 4): string {
  const negative = nano < 0n;
  const abs = negative ? -nano : nano;
  const whole = abs / NANO;
  const frac = abs % NANO;
  const fracStr = frac.toString().padStart(9, '0').slice(0, fractionDigits).replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole}${fracStr ? '.' + fracStr : ''}`;
}

export function parseTon(input: string): bigint {
  const trimmed = input.trim();
  if (!trimmed || !/^\d+(\.\d+)?$/.test(trimmed)) {
    throw new Error('invalid amount');
  }
  return toNano(trimmed);
}

/** Build Buy message body (matches BondingCurve.tact `Buy` message). */
export function buildBuyBody(args: {
  queryId: bigint;
  minOut: bigint;
  recipient: Address;
}): Cell {
  return beginCell()
    .storeUint(OP_BUY, 32)
    .storeUint(args.queryId, 64)
    .storeCoins(args.minOut)
    .storeAddress(args.recipient)
    .endCell();
}

/**
 * Build ClaimFees message body (matches BondingCurve.tact `ClaimFees`).
 *
 * The curve gates this on `sender() == royaltyReceiver`, so the wallet
 * signing it must already be the curve's royaltyReceiver — the royalty
 * claim flow rotates the receiver first (backend), then sends this.
 */
export function buildClaimFeesBody(args: { queryId: bigint }): Cell {
  return beginCell()
    .storeUint(OP_CLAIM_FEES, 32)
    .storeUint(args.queryId, 64)
    .endCell();
}

/**
 * CC-037 — forward_payload for a sell.
 *
 * A cell carrying `minTonOut` (slippage floor) as Coins. The bonding
 * curve parses this out of the JettonTransferNotification it receives
 * (`bonding_curve.tact` receive(JettonTransferNotification)).
 */
export function buildSellForwardPayload(minTonOut: bigint): Cell {
  return beginCell().storeCoins(minTonOut).endCell();
}

/**
 * CC-037 — TEP-74 jetton transfer body for selling into the bonding
 * curve.
 *
 * Sent to the SELLER's own jetton wallet, which moves the jettons to
 * the curve and pings it with a JettonTransferNotification. The curve's
 * notification receiver computes the TON payout (constant-product, 1%
 * fee) and sends TON back to the seller.
 *
 *   - `curveAddr`        → transfer `destination` (the curve).
 *   - `userAddr`         → `response_destination` (excess TON refund).
 *   - `forwardTonAmount` → must cover the notification + the curve's TON
 *                          return message (≥ 0.1 TON).
 *   - `minTonOut`        → slippage floor, carried in the forward payload.
 */
export function buildSellTransferBody(opts: {
  queryId: bigint;
  jettonAmount: bigint;
  curveAddr: Address;
  userAddr: Address;
  minTonOut: bigint;
  forwardTonAmount: bigint;
}): Cell {
  return beginCell()
    .storeUint(OP_JETTON_TRANSFER, 32) // 0xf8a7ea5 — TEP-74 transfer
    .storeUint(opts.queryId, 64)
    .storeCoins(opts.jettonAmount)
    .storeAddress(opts.curveAddr) // destination
    .storeAddress(opts.userAddr) // response_destination
    .storeMaybeRef(null) // custom_payload = null
    .storeCoins(opts.forwardTonAmount)
    .storeMaybeRef(buildSellForwardPayload(opts.minTonOut)) // forward_payload
    .endCell();
}

let qid = BigInt(Date.now()) * 1000n;
export function nextQueryId(): bigint {
  qid += 1n;
  return qid & ((1n << 64n) - 1n);
}
